/**
 * AJAR email-template helpers.
 *
 * Substitutes `{var}` placeholders in subject/body strings with provided values.
 * Used by mailto: link builders (Solicitudes, Consolidación reminder) to honor
 * the admin-edited templates served by GET /api/email-templates/{key}.
 */
import { api } from "@/lib/api";
import { toast } from "sonner";

const CHURCH_NAME = "Equipo AJAR";

/** Replace all `{key}` occurrences in `text` using values from `vars`. */
export function renderTemplate(text, vars) {
  if (!text) return "";
  return text.replace(/\{(\w+)\}/g, (_m, k) =>
    vars[k] !== undefined && vars[k] !== null && vars[k] !== ""
      ? String(vars[k])
      : "—",
  );
}

/** Fetch a saved template (with default fallback if backend unreachable). */
export async function fetchTemplate(key) {
  try {
    const { data } = await api.get(`/email-templates/${key}`);
    return data;
  } catch (e) {
    return null;
  }
}

/** All AJAR mailto: emails must reach the pastors' Gmail inbox. */
export const PASTORES_EMAIL = "pastoresTax@ministeriosajar.com";

/** Open a mailto: URL with subject/body already rendered. */
export function openMailto(to, subject, body) {
  let recipients = (to || "").trim();
  if (!recipients.toLowerCase().includes(PASTORES_EMAIL.toLowerCase())) {
    recipients = recipients ? `${recipients},${PASTORES_EMAIL}` : PASTORES_EMAIL;
  }
  const url = `mailto:${encodeURIComponent(recipients)}?subject=${encodeURIComponent(
    subject || "",
  )}&body=${encodeURIComponent(body || "")}`;

  // Heads-up toast: the OS mail client can take 1–2s to focus, which used to
  // make users double-click. Show a clear confirmation before navigation.
  toast.success("Abriendo cliente de correo…", {
    description: recipients
      ? `Para: ${recipients.length > 60 ? recipients.slice(0, 60) + "…" : recipients}`
      : undefined,
    duration: 2500,
  });

  // A programmatic anchor click is the most reliable way to launch the OS
  // mail handler. It works in iframes (Emergent preview), in sandboxed
  // contexts, and in production — whereas `window.location.href = "mailto:"`
  // is silently dropped inside sandboxed iframes.
  const a = document.createElement("a");
  a.href = url;
  a.rel = "noopener";
  // Keep the AJAR tab focused; the OS routes mailto: to the mail client.
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

/** Record an entry in the mailto log (fire-and-forget; never blocks UX). */
function logMailto(key, to, subject, body, ctx) {
  try {
    api.post("/mailto-logs", {
      template_key: key,
      recipient: to || "",
      subject: subject || "",
      body_preview: (body || "").slice(0, 500),
      context_label: (ctx && ctx.context_label) || "",
      context_id: (ctx && ctx.context_id) || null,
    }).catch((e) => {
      // Logging is best-effort — surface for diagnostics but never break flow.
      console.warn("mailto-logs: post failed", e);
    });
  } catch (e) {
    /* never throw from logging */
    console.warn("mailto-logs: unexpected error", e);
  }
}

/** Render and open a mailto using a saved template + vars; also logs it. */
export async function openMailtoFromTemplate(key, to, vars, ctx) {
  const tpl = await fetchTemplate(key);
  const fullVars = { iglesia: CHURCH_NAME, ...vars };
  const subject = tpl
    ? renderTemplate(tpl.subject, fullVars)
    : renderTemplate("AJAR — Notificación", fullVars);
  const body = tpl ? renderTemplate(tpl.body, fullVars) : "";
  logMailto(key, to, subject, body, ctx);
  openMailto(to, subject, body);
}

/** Variable lists exposed in the template editor UI. */
export const TEMPLATE_VARIABLES = {
  reminder_48h: ["iglesia", "cantidad", "lista_visitantes", "fecha_hoy"],
  request_decision: [
    "tipo",
    "titulo",
    "fecha",
    "hora",
    "lugar",
    "estado",
    "estado_mayus",
    "notas_admin",
    "iglesia",
  ],
  request_new: [
    "tipo",
    "titulo",
    "fecha",
    "hora",
    "lugar",
    "asistentes",
    "presupuesto",
    "responsable",
    "motivo",
    "solicitante",
    "iglesia",
  ],
  monthly_summary: [
    "mes",
    "anio",
    "emails_total",
    "recordatorios_48h",
    "solicitud_nueva",
    "decision_solicitud",
    "emails_otros",
    "solicitudes_total",
    "aprobadas",
    "rechazadas",
    "pendientes",
    "top_destinatarios",
    "iglesia",
  ],
};

/** Sample data used by the preview button. */
export const TEMPLATE_PREVIEW_SAMPLES = {
  reminder_48h: {
    iglesia: CHURCH_NAME,
    cantidad: 3,
    lista_visitantes:
      "  • Juan Pérez — +52 555 0000 — visitó 2026-01-30\n" +
      "  • María López — +52 555 1111 — visitó 2026-01-30\n" +
      "  • Carlos Ruiz — sin teléfono — visitó 2026-01-30",
    fecha_hoy: new Date().toISOString().slice(0, 10),
  },
  request_decision: {
    iglesia: CHURCH_NAME,
    tipo: "Salón",
    titulo: "Reunión de jóvenes",
    fecha: "2026-02-15",
    hora: "19:00",
    lugar: "Salón principal",
    estado: "aprobado",
    estado_mayus: "APROBADA",
    notas_admin: "Aprobado. Recordar montar sillas el día anterior.",
  },
  request_new: {
    iglesia: CHURCH_NAME,
    tipo: "Reunión",
    titulo: "Ensayo de alabanza",
    fecha: "2026-02-12",
    hora: "18:30",
    lugar: "Auditorio",
    asistentes: 12,
    presupuesto: "$50.00",
    responsable: "Pastor Andrés",
    motivo: "Preparación del culto dominical",
    solicitante: "secretaria@ajar.com",
  },
  monthly_summary: {
    iglesia: "AJAR — Ministerio Árboles Junto al Río",
    mes: "Enero",
    anio: "2026",
    emails_total: 14,
    recordatorios_48h: 5,
    solicitud_nueva: 4,
    decision_solicitud: 3,
    emails_otros: 2,
    solicitudes_total: 6,
    aprobadas: 4,
    rechazadas: 1,
    pendientes: 1,
    top_destinatarios:
      "  1. coordinador@ajar.com (6 envíos)\n" +
      "  2. secretaria@ajar.com (4 envíos)\n" +
      "  3. pastor@ajar.com (2 envíos)",
  },
};
